import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { userInfo, userToken } from "@finitless/shared/stores";
import { useApiStore } from "@finitless/shared/stores";
import { useToastContext } from "@finitless/shared/contexts";
import { apiClient } from "@finitless/shared/lib";

/**
 * Hook to handle integration actions (install, connect, setup)
 * @param {Object} params - Parameters for integration actions
 * @param {Object} params.business - Business object
 * @param {Function} params.onBusinessUpdate - Callback when business is updated
 * @param {Function} params.onIntegrationsUpdate - Callback when integrations are updated
 * @returns {Object} Integration action functions and state
 */
export const useIntegrationActions = ({
  business,
  onBusinessUpdate,
  onIntegrationsUpdate,
}) => {
  const [pluginSelected, setPluginSelected] = useState({
    open: false,
    title: "",
    url: "",
  });
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const user = userInfo();
  const token = userToken();
  const { project } = useApiStore();
  const { showToast } = useToastContext();

  /**
   * Check if integration is connected to business
   * @param {Object} integration - Integration object
   * @returns {boolean} True if connected
   */
  const isIntegrationConnected = (integration) => {
    if (
      !business?.third_party_integrations ||
      !Array.isArray(business.third_party_integrations)
    ) {
      return false;
    }

    return business.third_party_integrations.some(
      (item) => item?.third_party_integration_id === integration?.id,
    );
  };

  /**
   * Get the text for the action button
   * @param {Object} integration - Integration object
   * @returns {string} Button text
   */
  const getButtonText = (integration) => {
    if (!integration?.plugin_id) {
      return business?.id ? "Install & Connect" : "Install";
    }

    const isConnected = isIntegrationConnected(integration);
    return isConnected ? "POS setup" : "Connect";
  };

  /**
   * Check if setup button should be hidden
   * @param {Object} integration - Integration object
   * @returns {boolean} True if should hide button
   */
  const shouldHideSetupButton = (integration) => {
    const isConnected = isIntegrationConnected(integration);
    return isConnected && !integration?.plugin?.setup_url;
  };

  /**
   * Parse template variables in URLs
   * @param {string} template - URL template
   * @param {Object} variables - Variables to replace
   * @param {RegExp} regex - Regex pattern for replacement
   * @returns {string} Parsed URL
   */
  const parseTemplate = (template, variables, regex = /@\{([^}]+)\}/g) => {
    return template.replace(regex, (match, key) => {
      return variables[key] || match;
    });
  };

  /**
   * Handle sync URL generation
   * @param {string} urlParam - URL parameter from plugin
   * @param {string} root - Root URL from plugin
   * @returns {string} Generated URL
   */
  const handleSync = async (urlParam, root) => {
    const params = {
      business_id: business?.id,
      user_id: user?.id,
      project: project,
      token,
    };

    const transformedParams = Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join("&");

    let url = `https://integrations.ordering.co/clover-store/sync.php?${transformedParams}`;

    if (root && urlParam) {
      url = `${root}${urlParam
        .replace("@{project_code}", project || "")
        .replace("@{access_token}", token || "")
        .replace("@{business_id}", business?.id || "")}`;
    }

    return url;
  };

  /**
   * Install system plugin
   * @param {string} pluginUrl - Plugin URL to install
   * @param {Function} callback - Success callback
   */
  const handleInstallSysPlugin = async (pluginUrl, callback) => {
    try {
      setLoading(true);
      showToast("Installing plugin...", "info");

      const response = await apiClient.post("/plugins", {
        url: pluginUrl,
      });

      // Invalidate thirdPartyIntegrations cache so the UI updates to show the newly installed plugin
      queryClient.invalidateQueries({
        queryKey: ["thirdPartyIntegrations"],
      });

      showToast("Plugin installed successfully", "success");
      callback && callback(response);
    } catch (error) {
      showToast(error.message || "Failed to install plugin", "error");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Connect business to integration
   * @param {string|number} integrationId - Integration ID
   * @param {Object} data - Data to send
   * @param {Function} callback - Success callback
   * @param {string} successMessage - Custom success message
   */
  const handleConnectBusiness = async (
    integrationId,
    data,
    callback,
    successMessage,
  ) => {
    try {
      setLoading(true);
      const response = await apiClient.post(
        `/business/${business?.id}/third_party_integrations/${integrationId}`,
        data,
      );

      // Update business-by-slug cache optimistically with new integration
      if (business?.slug && response?.result) {
        queryClient.setQueryData(
          ["business-by-slug", business.slug],
          (oldData) => {
            if (!oldData) return oldData;
            const existingIntegrations = oldData.third_party_integrations || [];
            // Check if integration already exists
            const integrationExists = existingIntegrations.some(
              (integration) =>
                integration.third_party_integration_id === integrationId,
            );
            if (integrationExists) {
              // Update existing integration
              return {
                ...oldData,
                third_party_integrations: existingIntegrations.map(
                  (integration) =>
                    integration.third_party_integration_id === integrationId
                      ? { ...integration, ...response.result }
                      : integration,
                ),
              };
            } else {
              // Add new integration
              return {
                ...oldData,
                third_party_integrations: [
                  ...existingIntegrations,
                  response.result,
                ],
              };
            }
          },
        );
      }

      // Invalidate and refetch queries to ensure consistency
      if (business?.slug) {
        queryClient.invalidateQueries({
          queryKey: ["business-by-slug", business.slug],
        });
        // Force refetch immediately
        queryClient.refetchQueries({
          queryKey: ["business-by-slug", business.slug],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["businesses"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["businesses-products"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["thirdPartyIntegrations"],
        exact: false,
      });

      showToast(successMessage || "Business connected successfully", "success");
      callback && callback();
    } catch (error) {
      // Rollback optimistic update on error
      if (business?.slug) {
        queryClient.invalidateQueries({
          queryKey: ["business-by-slug", business.slug],
        });
      }
      showToast(error.message || "Failed to connect business", "error");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Disconnect business from integration
   * @param {string|number} integrationId - Integration ID
   * @param {Function} callback - Success callback
   */
  const handleDisconnectBusiness = async (integrationId, callback) => {
    try {
      setLoading(true);

      // Optimistic update: remove integration from business-by-slug cache
      if (business?.slug) {
        queryClient.setQueryData(
          ["business-by-slug", business.slug],
          (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              third_party_integrations: (
                oldData.third_party_integrations || []
              ).filter(
                (integration) =>
                  integration.third_party_integration_id !== integrationId,
              ),
            };
          },
        );
      }

      await apiClient.delete(
        `/business/${business?.id}/third_party_integrations/${integrationId}`,
      );

      // Invalidate and refetch queries to ensure consistency
      if (business?.slug) {
        queryClient.invalidateQueries({
          queryKey: ["business-by-slug", business.slug],
        });
        // Force refetch immediately
        queryClient.refetchQueries({
          queryKey: ["business-by-slug", business.slug],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["businesses"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["businesses-products"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["thirdPartyIntegrations"],
        exact: false,
      });

      showToast("Integration disconnected successfully", "success");
      callback && callback();
    } catch (error) {
      // Rollback optimistic update on error
      if (business?.slug) {
        queryClient.invalidateQueries({
          queryKey: ["business-by-slug", business.slug],
        });
      }
      showToast(error.message || "Failed to disconnect integration", "error");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Install plugin and connect business in a single step
   * @param {Object} integration - Integration object (must have plugin_root and id)
   */
  const handleInstallAndConnect = async (integration) => {
    try {
      setLoading(true);
      showToast("Setting up integration...", "info");

      // Step 1: Install the plugin
      const installResponse = await apiClient.post("/plugins", {
        url: integration.plugin_root,
      });
      const installedPlugin = installResponse?.result;

      // Step 2: Connect the business to the integration
      const connectResponse = await apiClient.post(
        `/business/${business?.id}/third_party_integrations/${integration.id}`,
        { enabled: true },
      );

      // Update business-by-slug cache optimistically
      if (business?.slug && connectResponse?.result) {
        queryClient.setQueryData(
          ["business-by-slug", business.slug],
          (oldData) => {
            if (!oldData) return oldData;
            const existingIntegrations = oldData.third_party_integrations || [];
            const integrationExists = existingIntegrations.some(
              (i) => i.third_party_integration_id === integration.id,
            );
            if (integrationExists) {
              return {
                ...oldData,
                third_party_integrations: existingIntegrations.map((i) =>
                  i.third_party_integration_id === integration.id
                    ? { ...i, ...connectResponse.result }
                    : i,
                ),
              };
            }
            return {
              ...oldData,
              third_party_integrations: [
                ...existingIntegrations,
                connectResponse.result,
              ],
            };
          },
        );
      }

      // Await critical refetches so the UI has fresh data before loading ends
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: ["thirdPartyIntegrations"],
          exact: false,
        }),
        queryClient.refetchQueries({
          queryKey: ["businesses-integrations"],
          exact: false,
        }),
      ]);

      // Enrich integration with plugin data from install response if refetch returned incomplete data
      if (installedPlugin) {
        queryClient.setQueryData(["thirdPartyIntegrations"], (oldData) => {
          if (!oldData?.integrations) return oldData;
          return {
            ...oldData,
            integrations: oldData.integrations.map((i) => {
              if (i.id === integration.id && !i.plugin?.root) {
                return {
                  ...i,
                  plugin_id: installedPlugin.id || i.plugin_id,
                  plugin: { ...i.plugin, ...installedPlugin },
                };
              }
              return i;
            }),
          };
        });
      }

      // Invalidate secondary caches (non-blocking)
      if (business?.slug) {
        queryClient.invalidateQueries({
          queryKey: ["business-by-slug", business.slug],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["businesses-products"],
        exact: false,
      });

      showToast("Integration connected successfully", "success");
    } catch (error) {
      // Refetch so UI reflects partial progress (e.g., plugin installed but connect failed)
      await queryClient.refetchQueries({
        queryKey: ["thirdPartyIntegrations"],
        exact: false,
      });

      if (business?.slug) {
        queryClient.invalidateQueries({
          queryKey: ["business-by-slug", business.slug],
        });
      }

      showToast(error.message || "Failed to set up integration", "error");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Open plugin details in modal
   * @param {Event} e - Click event
   * @param {Object} plugin - Plugin object
   */
  const handleOpenPluginDetails = async (e, plugin) => {
    if (!plugin) return;

    e.stopPropagation();

    try {
      const url = await handleSync(plugin?.setup_url, plugin?.root);

      setPluginSelected({
        open: true,
        title: plugin.name,
        url,
      });
    } catch {
      showToast("Failed to open plugin details", "error");
    }
  };

  /**
   * Handle button click with improved logic
   * @param {Object} integration - Integration object
   * @param {Event} e - Click event
   */
  const handleButtonClick = async (integration, e) => {
    e.stopPropagation();

    // Case 1: Install system plugin (has plugin_root but no plugin_id)
    if (integration?.plugin_root && !integration?.plugin_id) {
      if (business?.id) {
        // Install + connect in one step when a business is selected
        return handleInstallAndConnect(integration);
      }
      // Fallback: install only if no business context
      return handleInstallSysPlugin(integration?.plugin_root, () => {
        onIntegrationsUpdate && onIntegrationsUpdate();
      });
    }

    // Case 2: Plugin not found
    if (!integration?.plugin_root && !integration?.plugin_id) {
      showToast("Plugin is needed to connect to this integration", "error");
      return;
    }

    // Case 3: Has plugin_id - either connect or setup
    if (integration?.plugin_id) {
      const isConnected = isIntegrationConnected(integration);

      if (!isConnected) {
        // Connect business to integration
        return handleConnectBusiness(integration?.id, { enabled: true }, () => {
          // Only refresh business data after connecting (business now has new integration)
          onBusinessUpdate && onBusinessUpdate();
        });
      } else {
        // Open setup modal
        return handleOpenPluginDetails(e, integration?.plugin);
      }
    }
  };

  /**
   * Close plugin modal
   */
  const handleClosePluginModal = () => {
    setPluginSelected({
      open: false,
      title: "",
      url: "",
    });
  };

  /**
   * Get variables for template parsing
   * @returns {Object} Variables object
   */
  const getVariables = () => ({
    project_code: project || "",
    access_token: token || "",
    business_id: business?.id || "",
    user_id: user?.id || "",
  });

  return {
    // State
    pluginSelected,
    loading,

    // Helper functions
    isIntegrationConnected,
    getButtonText,
    shouldHideSetupButton,

    // Action functions
    handleButtonClick,
    handleOpenPluginDetails,
    handleClosePluginModal,
    handleInstallSysPlugin,
    handleInstallAndConnect,
    handleConnectBusiness,
    handleDisconnectBusiness,

    // Utility functions
    parseTemplate,
    getVariables,
  };
};

export default useIntegrationActions;
